import React from 'react';
import { determineGlobalFilterDefinitions } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineSecondaryFilterDefinitionsForFeature } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedGlobalFilterValue } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { EntitledDetailsInContextOfFiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { GROUP_BY_DEFAULT_ITEM_KEY } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedEventStudyGroupFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedSingleDateFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedPipelineAnalysisConfiguration } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedStringValuePrimaryFilterFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedMetric } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedMetrics } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedDimension } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedFocusArea } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedComparisonGroup } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedEmployeeGroup } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedSecondaryFilterValue } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { PrimaryFilterKey } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { GetLevelByColumnValueFn } from 'libs/end-to-end/src/dimension';
import { inferTypeForFilterStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { MetricDefinitionWhiteLabeling } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { tryTranslateDynamic } from 'libs/end-to-end/src/phrase-utils';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { getAllowedGroupBySelectionsForTableView } from 'libs/end-to-end/src/table-view-utils';
import { GroupBySelectionForTableView } from 'libs/end-to-end/src/table-view-utils';
import { ViewByOption } from 'libs/end-to-end/src/view-by';
import { viewByOptionToText } from 'libs/end-to-end/src/view-by';
import { BigTimePeriodComparison } from 'libs/end-to-end/src/big-time-period-comparison';
import { bigTimePeriodComparisonToText } from 'libs/end-to-end/src/big-time-period-comparison';
import { IconCalendarFilterSvg } from 'apps/sigma2-ui/src/filters-v2/components/IconCalendarFilter';
import { IconGroupsFilterSvg } from 'apps/sigma2-ui/src/filters-v2/components/IconGroupsFilter';
import { EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE } from 'libs/end-to-end/src/progress';

export interface FilterTranslations {
  leftTranslatedContent: TranslatedContent;
  rightTranslatedContent: TranslatedContent;
  hasSelection: boolean;
}

// eslint-disable-next-line complexity
export const getTranslationsForPrimaryFilterButton = ({
  primaryFilterKey,
  feature,
  store,
  organizationId,
  focusArea,
  analysisName,
  databaseId,
  databaseRevisionId,
  getLevelByColumnValue,
  whiteLabelForMetric,
  myObservation,
  calibrationFraming,
  allDimensions,
  secondaryFilterDefinitions,
  entitledFilterFeatureDetails,
  language,
}: {
  primaryFilterKey: PrimaryFilterKey;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  store: FilterStoreHook;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  getLevelByColumnValue: GetLevelByColumnValueFn;
  whiteLabelForMetric: MetricDefinitionWhiteLabeling | null;
  myObservation: MyObservation | null;
  calibrationFraming: CalibrationFraming;
  language: Phrases;
  allDimensions: DimensionDefinition[];
  secondaryFilterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'];
  entitledFilterFeatureDetails: EntitledDetailsInContextOfFiltersFeatureName;
}): FilterTranslations | null => {
  const selectedGroup = getSelectedEmployeeGroup({
    focusArea,
    analysisName,
    databaseRevisionId,
    databaseId,
    feature,
    store,
    organizationId,
  });

  const selectedDimensionValue = getSelectedDimension({
    feature,
    store,
    organizationId,
    databaseId,
    databaseRevisionId,
    focusArea,
    analysisName,
  });

  if (primaryFilterKey === 'employeeGroup') {
    const levelDefinition = selectedGroup
      ? getLevelByColumnValue(selectedGroup.dimension, selectedGroup.level)
      : null;

    return {
      leftTranslatedContent: language.employeeGroup({
        subjectName: whiteLabelForMetric?.subjectSingular ?? null,
        calibrationFraming,
      }),
      rightTranslatedContent: levelDefinition
        ? language.levelDisplayName({
            definition: levelDefinition,
          })
        : language.noSelection(),
      hasSelection: !!levelDefinition,
    };
  }

  if (primaryFilterKey === 'comparisonGroup') {
    const selectedComparisonGroup = getSelectedComparisonGroup({
      feature,
      store,
      organizationId,
      focusArea,
      analysisName,
      databaseId,
      databaseRevisionId,
    });

    const dimension = selectedGroup?.dimension ?? selectedDimensionValue;

    const levelDefinition =
      selectedComparisonGroup && dimension
        ? getLevelByColumnValue(dimension, selectedComparisonGroup)
        : null;

    return {
      leftTranslatedContent: language.comparisonGroup(),
      rightTranslatedContent: levelDefinition
        ? language.levelDisplayName({
            definition: levelDefinition,
          })
        : language.noSelection(),
      hasSelection: !!levelDefinition,
    };
  }

  if (primaryFilterKey === 'focusArea') {
    const selectedOption = getSelectedFocusArea({
      feature,
      store,
      organizationId,
      databaseId,
      databaseRevisionId,
    });

    const selectedFocusArea = selectedOption
      ? myObservation?.focusAreas.find(
          (fa) => fa.focusArea === selectedOption.focusArea,
        )
      : null;

    const selectedMetric = selectedFocusArea?.metrics.find(
      (m) => m.columnHeaderValue === selectedOption?.metric,
    );

    return {
      leftTranslatedContent: language.metric(),
      rightTranslatedContent: selectedMetric
        ? language.metricDisplayName({
            definition: selectedMetric,
          })
        : language.noSelection(),
      hasSelection: !!selectedMetric,
    };
  }

  if (primaryFilterKey === 'dimension') {
    const dimension = myObservation?.dimensions.find(
      (d) => d.columnHeaderValue === selectedDimensionValue,
    );

    return {
      leftTranslatedContent: language.dimension(),
      rightTranslatedContent: dimension
        ? language.dimensionDisplayName({
            definition: dimension,
          })
        : language.noSelection(),
      hasSelection: !!dimension,
    };
  }

  const selectedMetricValue = getSelectedMetric({
    feature,
    store,
    organizationId,
    databaseId,
    databaseRevisionId,
    focusArea,
    analysisName,
  });

  const selectedMetricDefinition =
    entitledFilterFeatureDetails.focusAreaMetrics.find(
      (m) => m.columnHeaderValue === selectedMetricValue,
    );

  if (primaryFilterKey === 'metric') {
    return {
      leftTranslatedContent: language.metric(),
      rightTranslatedContent:
        selectedMetricValue && selectedMetricDefinition && focusArea
          ? language.metricDisplayName({
              definition: selectedMetricDefinition,
            })
          : language.noSelection(),
      hasSelection:
        !!selectedMetricValue && !!selectedMetricDefinition && !!focusArea,
    };
  }

  const selectedMetrics =
    getSelectedMetrics({
      feature,
      store,
      organizationId,
      databaseId,
      databaseRevisionId,
    }) ?? [];

  const allMetrics = myObservation?.focusAreas.flatMap((fa) => fa.metrics);

  const selectedMetricsDefinition =
    allMetrics?.filter((m) => selectedMetrics.includes(m.columnHeaderValue)) ??
    [];

  if (primaryFilterKey === 'metrics') {
    return {
      leftTranslatedContent:
        feature === 'geolocation'
          ? language.events()
          : language.metricBarSelectLabel(),
      rightTranslatedContent: language.filterMultiSelectDisplayPreview({
        items: selectedMetricsDefinition.map((m) =>
          feature === 'geolocation'
            ? language.metricEventDisplayName({
                definition: m,
              })
            : language.metricDisplayName({
                definition: m,
              }),
        ),
      }),
      hasSelection: true,
    };
  }

  if (primaryFilterKey === 'pipelineAnalysisConfiguration') {
    const selectedPipelineAnalysisConfigurationName =
      getSelectedPipelineAnalysisConfiguration({
        feature,
        store,
        organizationId,
        databaseId,
        databaseRevisionId,
        focusArea,
        analysisName,
      });

    const pipelineAnalysisConfigurations =
      myObservation?.pipelineAnalysisConfiguration ?? [];
    const pipelineAnalysisConfiguration = pipelineAnalysisConfigurations.find(
      (p) => p.name === selectedPipelineAnalysisConfigurationName,
    );

    return {
      leftTranslatedContent: language.pipelineAnalysis(),
      rightTranslatedContent: pipelineAnalysisConfiguration
        ? tryTranslateDynamic({
            logger: uiAppLogger,
            phrase: pipelineAnalysisConfiguration.name,
            language,
            customValue:
              pipelineAnalysisConfiguration.customFirstPhaseName ?? '',
          })
        : language.noSelection(),
      hasSelection: !!selectedPipelineAnalysisConfigurationName,
    };
  }

  if (primaryFilterKey === 'groupBy') {
    const selectedGroupBy = getSelectedStringValuePrimaryFilterFromStore({
      store,
      feature,
      focusArea,
      analysisName,
      organizationId,
      databaseRevisionId,
      databaseId,
      primaryFilterKey: 'groupBy',
    });

    const allowedItems = getAllowedGroupBySelectionsForTableView({
      allDimensions,
      secondaryFilterDefinitions,
      language,
      includeNoSelection: false,
    });

    if (feature === 'timeSeriesTrendsPrimary') {
      //add overall average option
      const overallAverageItem: GroupBySelectionForTableView = {
        value: GROUP_BY_DEFAULT_ITEM_KEY,
        displayName: 'Overall Average',
        dimensionDefinition: null,
        filterDefinition: null,
      };
      allowedItems.unshift(overallAverageItem);
    }

    return {
      leftTranslatedContent: language.groupBy(),
      rightTranslatedContent:
        allowedItems.find((i) => i.value === selectedGroupBy)?.displayName ??
        language.noSelection(),
      hasSelection: !!selectedGroupBy,
    };
  }

  if (primaryFilterKey === 'secondaryBreakdown') {
    const selectedSecondaryBreakdown =
      getSelectedStringValuePrimaryFilterFromStore({
        store,
        feature,
        focusArea,
        analysisName,
        organizationId,
        databaseRevisionId,
        databaseId,
        primaryFilterKey: 'secondaryBreakdown',
      });

    const allowedItems = getAllowedGroupBySelectionsForTableView({
      allDimensions,
      secondaryFilterDefinitions,
      language,
      includeNoSelection: true,
    });

    return {
      leftTranslatedContent: language.secondaryBreakdownFilter(),
      rightTranslatedContent:
        (selectedSecondaryBreakdown &&
          allowedItems.find((i) => i.value === selectedSecondaryBreakdown)
            ?.displayName) ??
        language.noSelection(),
      hasSelection: !!selectedSecondaryBreakdown,
    };
  }

  if (primaryFilterKey === 'viewBy') {
    const selectedOption = getSelectedStringValuePrimaryFilterFromStore({
      store,
      feature,
      focusArea,
      analysisName,
      organizationId,
      databaseRevisionId,
      databaseId,
      primaryFilterKey: 'viewBy',
    });

    const parseResult = selectedOption
      ? ViewByOption.safeParse(selectedOption)
      : null;
    if (parseResult && parseResult.success) {
      return {
        leftTranslatedContent: 'View by',
        rightTranslatedContent: viewByOptionToText[parseResult.data],
        hasSelection: true,
      };
    } else {
      return {
        leftTranslatedContent: 'View by',
        rightTranslatedContent: 'No selection',
        hasSelection: false,
      };
    }
  }

  if (primaryFilterKey === 'bigTimePeriodComparison') {
    const selectedOption = getSelectedStringValuePrimaryFilterFromStore({
      store,
      feature,
      focusArea,
      analysisName,
      organizationId,
      databaseRevisionId,
      databaseId,
      primaryFilterKey: 'bigTimePeriodComparison',
    });

    const parseResult = selectedOption
      ? BigTimePeriodComparison.safeParse(selectedOption)
      : null;
    if (parseResult && parseResult.success && parseResult.data !== 'none') {
      return {
        leftTranslatedContent: 'Compare with',
        rightTranslatedContent: bigTimePeriodComparisonToText[parseResult.data],
        hasSelection: true,
      };
    } else {
      return {
        leftTranslatedContent: 'Compare with',
        rightTranslatedContent: 'No selection',
        hasSelection: false,
      };
    }
  }

  if (primaryFilterKey === 'singleDate') {
    const selectedOption = getSelectedSingleDateFromStore({
      store,
      feature,
      organizationId,
      databaseRevisionId,
      databaseId,
    });

    return {
      leftTranslatedContent: language.selectSingleDate(),
      rightTranslatedContent:
        selectedOption ?? EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE,
      hasSelection: !!selectedOption,
    };
  }

  if (primaryFilterKey === 'eventStudyGroup') {
    const selectedEventStudyGroup = getSelectedEventStudyGroupFromStore({
      databaseRevisionId,
      databaseId,
      feature,
      store,
      organizationId,
    });
    return {
      leftTranslatedContent: language.selectEventStudyGroup(),
      rightTranslatedContent:
        selectedEventStudyGroup?.dimensionOrFilterValue ??
        language.noSelection(),
      hasSelection: !!selectedEventStudyGroup,
    };
  }

  return null;
};

export const getIconForPrimaryFilterButton = ({
  primaryFilterKey,
}: {
  primaryFilterKey: PrimaryFilterKey;
}) => {
  if (primaryFilterKey === 'eventStudyGroup') {
    return <IconGroupsFilterSvg />;
  }

  if (primaryFilterKey === 'singleDate') {
    return <IconCalendarFilterSvg />;
  }

  return undefined;
};

export const getTranslationsForSecondaryFilterChip = ({
  secondaryFilterKey,
  feature,
  focusArea,
  analysisName,
  myObservation,
  language,
  currentValue,
}: {
  secondaryFilterKey: string;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  myObservation: MyObservation | null;
  language: Phrases;
  currentValue: FilterStoreValue | null;
}): FilterTranslations | null => {
  const secondaryFilterDefinitions =
    determineSecondaryFilterDefinitionsForFeature({
      feature,
      focusArea,
      analysisName,
      focusAreas: myObservation?.focusAreas ?? [],
      filterSetDefinitionsWithValues:
        myObservation?.filterSetDefinitionsWithValues ?? [],
      dimensionDefinitions: myObservation?.dimensions ?? [],
      pipelineAnalysisConfiguration:
        myObservation?.pipelineAnalysisConfiguration,
    });

  const definition = secondaryFilterDefinitions.find(
    (definition) => definition.columnHeaderValue === secondaryFilterKey,
  );

  if (!definition) {
    return null;
  }

  return {
    leftTranslatedContent: language.filterDefinitionDisplayName({ definition }),
    rightTranslatedContent: convertFilterStoreValueToDisplayTranslation({
      value: currentValue,
      language,
      optionalDisplayValues: definition.optionalDisplayValues,
    }),
    hasSelection: currentValue !== null,
  };
};

export const getTranslationsForGlobalFilterChip = ({
  filterKey,
  store,
  organizationId,
  databaseId,
  databaseRevisionId,
  myObservation,
  language,
  scopeId,
}: {
  filterKey: string;
  store: FilterStoreHook;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  myObservation: MyObservation | null;
  language: Phrases;
  scopeId: string;
}): FilterTranslations | null => {
  const currentValue = getSelectedGlobalFilterValue({
    store,
    databaseId,
    databaseRevisionId,
    organizationId,
    filterKey,
    scopeId,
  });

  const globalFilterDefinitions = determineGlobalFilterDefinitions({
    filterSetDefinitionsWithValues:
      myObservation?.filterSetDefinitionsWithValues ?? [],
  });

  const definition = globalFilterDefinitions.find(
    (definition) => definition.columnHeaderValue === filterKey,
  );

  if (!definition) {
    return null;
  }

  return {
    leftTranslatedContent: language.filterDefinitionDisplayName({ definition }),
    rightTranslatedContent: convertFilterStoreValueToDisplayTranslation({
      value: currentValue,
      language,
      optionalDisplayValues: definition.optionalDisplayValues,
    }),
    hasSelection: currentValue !== null,
  };
};

export const convertFilterStoreValueToDisplayTranslation = ({
  value,
  language,
  optionalDisplayValues,
}: {
  value: FilterStoreValue | null;
  optionalDisplayValues: Record<string, string> | undefined;
  language: Phrases;
}): TranslatedContent => {
  if (value === null) {
    return language.noSelection();
  }

  const type = inferTypeForFilterStore(value);

  if (type.type === 'string') {
    const displayValue = optionalDisplayValues?.[type.string] ?? type.string;

    return language.dangerousUserInput({
      value: displayValue,
    });
  }

  if (type.type === 'string[]') {
    return language.filterMultiSelectDisplayPreview({
      items: type.stringArray.map(
        (item) => optionalDisplayValues?.[item] ?? item,
      ),
    });
  }

  if (type.type === '[number, number]') {
    return language.numberRangeDisplay({
      start: type.numberRange[0],
      end: type.numberRange[1],
    });
  }

  if (type.type === 'date') {
    return language.dateDisplay({
      date: type.date,
    });
  }

  return language.noSelection();
};
